// @ts-strict-ignore
import invariant from 'invariant';
import { Dispatch } from 'redux';

import { fetchUserEnvironment } from 'api/apiUserEnvironment';
import { SearchResults } from 'api/search';
import {
  MainStoreUserEnvironmentAction,
  MainStoreUserEnvironmentState,
  updateUserEnvironment,
} from 'modules/main/store/ducks/userEnvironment';
import algoliaSearch from 'utils/algoliaSearch';
import { configString } from 'utils/config';
import getSearchConfig from 'utils/search/getSearchConfig';

// Types

export type MainStoreOrgDashboardSearchState = {
  orgId?: string;
  apiKey?: string;
  pageIndex: number;
  type: string;
  status: string;
  query: string;
  results: SearchResults | null | undefined;
  typeCounts: Record<string, number> | null | undefined;
  filters: {
    isFetching: boolean;
    status: Array<string>;
  };
};

type SearchStartAction = {
  type: 'ORG_DASHBOARD_SEARCH_START';
  orgId: string;
  apiKey: string;
  listingType: string;
  status: string;
  query: string;
  pageIndex: number;
};

type SearchSuccessAction = {
  type: 'ORG_DASHBOARD_SEARCH_SUCCESS';
  results: SearchResults;
};

type SetTypeCountsAction = {
  type: 'ORG_DASHBOARD_SET_TYPE_COUNTS';
  typeCounts: Record<string, number>;
};

type FacetsStartAction = {
  type: 'ORG_DASHBOARD_FACETS_START';
};

type FacetsSuccessAction = {
  type: 'ORG_DASHBOARD_FACETS_SUCCESS';
  facets: {
    status: Record<string, number>;
  };
};

export type MainStoreOrgDashboardSearchAction =
  | SearchSuccessAction
  | SearchStartAction
  | SetTypeCountsAction
  | FacetsStartAction
  | FacetsSuccessAction;

// Reducer

const initialState = {
  pageIndex: 0,
  type: 'ALL',
  status: 'ALL',
  query: '',
  results: null,
  typeCounts: null,
  filters: {
    status: [],
    isFetching: false,
  },
};

export function searchReducer(
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/default-param-last
  state: MainStoreOrgDashboardSearchState = initialState,
  action: MainStoreOrgDashboardSearchAction
) {
  switch (action.type) {
    case 'ORG_DASHBOARD_SEARCH_START': {
      const { orgId, apiKey, listingType, status, query, pageIndex } = action;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line prefer-object-spread
      return Object.assign({}, state, {
        orgId,
        apiKey,
        pageIndex,
        type: listingType,
        status,
        query,
        results: state.results,
        typeCounts: orgId === state.orgId ? state.typeCounts : null,
      });
    }

    case 'ORG_DASHBOARD_SEARCH_SUCCESS':
      return { ...state, results: action.results };

    case 'ORG_DASHBOARD_SET_TYPE_COUNTS':
      return { ...state, typeCounts: action.typeCounts };

    case 'ORG_DASHBOARD_FACETS_START': {
      return { ...state, filters: { ...state.filters, isFetching: true } };
    }

    case 'ORG_DASHBOARD_FACETS_SUCCESS': {
      const orgStatuses: Array<string> = Object.keys(
        action.facets.status || {}
      );
      return {
        ...state,
        filters: {
          status: orgStatuses,
          isFetching: false,
        },
      };
    }

    default:
      return state;
  }
}

// Actions

const getQueryOptions = ({
  type,
  status,
  pageIndex,
}: {
  type: string;
  status: string;
  pageIndex: number;
}): QueryOptions => {
  const filters = [];

  if (type === 'EVENT') {
    filters.push(`actionType:'${type}'`);
  } else if (type === 'VOLOP') {
    filters.push(`type: ${type} AND NOT actionType:EVENT`);
  } else if (type !== 'ALL') {
    filters.push(`type:'${type}'`);
  }

  if (status !== 'ALL') {
    filters.push(`status:'${status}'`);
  }

  const attributesToRetrieve = [
    'objectID',
    'type',
    'status',
    'name',
    'city',
    'state',
    'country',
    'published',
    'created',
    'expires',
    'updated',
    'inCartOf',
    'uniqueViews',
    'applicationCount',
    'actionType',
  ];
  return {
    index: configString('algolia', 'orgIndexName'),
    hitsPerPage: 20,
    page: pageIndex,
    attributesToRetrieve,
    filters: filters.join(' AND '),
  };
};

const fetchTypeCounts = (
  apiKey: string,
  orgId: string,
  dispatch: Dispatch<
    MainStoreOrgDashboardSearchAction | MainStoreUserEnvironmentAction
  >
) => {
  const searchConfig = getSearchConfig(apiKey);
  const queryOptions: QueryOptions = {
    index: configString('algolia', 'orgIndexName'),
    facets: ['type'],
    hitsPerPage: 0,
  };
  return algoliaSearch(searchConfig, '', queryOptions, false, () =>
    fetchUserEnvironment().then((fetchedUserEnvironment) => {
      dispatch(updateUserEnvironment(fetchedUserEnvironment));
      return fetchedUserEnvironment.user.orgs.filter((o) => o.id === orgId)[0]
        .searchApiKey;
    })
  )
    .then(({ facets: { type } }) => ({
      JOB: (type && type.JOB) || 0,
      INTERNSHIP: (type && type.INTERNSHIP) || 0,
      VOLOP: (type && type.VOLOP) || 0,
    }))
    .catch((error) => error);
};

export const search =
  ({
    orgId,
    query,
    status,
    type,
    pageIndex,
    force,
  }: {
    orgId: string;
    query: string;
    status: string;
    type: string;
    pageIndex: number;
    force: boolean;
  }) =>
  (
    dispatch: Dispatch<
      MainStoreOrgDashboardSearchAction | MainStoreUserEnvironmentAction
    >,
    getState: () => {
      orgDashboard: { search: MainStoreOrgDashboardSearchState };
      userEnvironment: MainStoreUserEnvironmentState;
    }
  ) => {
    const {
      orgDashboard: { search: searchInfo },
      userEnvironment: {
        user: { orgs },
      },
    } = getState();

    if (
      orgId === searchInfo.orgId &&
      status === searchInfo.status &&
      type === searchInfo.type &&
      query === searchInfo.query &&
      pageIndex === searchInfo.pageIndex &&
      searchInfo.results &&
      !force
    ) {
      return;
    }

    const org = orgs && orgs.find((o) => o.id === orgId);
    invariant(org, 'Invalid org id');
    const apiKey = org.searchApiKey;

    if (orgId !== searchInfo.orgId || !searchInfo.typeCounts) {
      fetchTypeCounts(apiKey, orgId, dispatch).then((typeCounts) => {
        dispatch({
          type: 'ORG_DASHBOARD_SET_TYPE_COUNTS',
          typeCounts,
        });
      });
    }

    dispatch({
      type: 'ORG_DASHBOARD_SEARCH_START',
      orgId,
      apiKey,
      listingType: type,
      status,
      query,
      pageIndex,
    });
    const queryOptions = getQueryOptions({
      type,
      status,
      pageIndex,
    });
    const searchConfig = getSearchConfig(apiKey);

    algoliaSearch(searchConfig, query, queryOptions, true, () =>
      fetchUserEnvironment().then((fetchedUserEnvironment) => {
        dispatch(updateUserEnvironment(fetchedUserEnvironment));
        return fetchedUserEnvironment.user.orgs.filter((o) => o.id === orgId)[0]
          .searchApiKey;
      })
    )
      .then((results) => {
        dispatch({
          type: 'ORG_DASHBOARD_SEARCH_SUCCESS',
          // @ts-expect-error fix me
          results,
        });
      })
      .catch((error) => {
        throw error;
      });
  };
export const fetchFacets =
  (orgId: string) =>
  (
    dispatch: Dispatch<
      MainStoreOrgDashboardSearchAction | MainStoreUserEnvironmentAction
    >,
    getState: () => {
      orgDashboard: { search: MainStoreOrgDashboardSearchState };
      userEnvironment: MainStoreUserEnvironmentState;
    }
  ) => {
    const {
      orgDashboard: {
        search: {
          filters: { isFetching },
        },
      },
      userEnvironment: {
        user: { orgs },
      },
    } = getState();
    const index = configString('algolia', 'orgIndexName');
    invariant(orgId, 'orgId is not set');
    const org = orgs && orgs.find((o) => o.id === orgId);
    invariant(org, 'Invalid org id');
    const apiKey = org.searchApiKey;
    if (isFetching) return;
    dispatch({
      type: 'ORG_DASHBOARD_FACETS_START',
    });
    const searchConfig = getSearchConfig(apiKey);
    algoliaSearch(
      searchConfig,
      '',
      {
        index,
        facets: ['*'],
        hitsPerPage: 0,
        page: 0,
        attributesToRetrieve: ['*'],
        // if they are staff then we need to filter results by org Id
        filters: '', // if staff is added, use the following addition => staff ? `orgID:'${orgId}'` : ''
      } as QueryOptions,
      false,
      () =>
        fetchUserEnvironment().then((fetchedUserEnvironment) => {
          dispatch(updateUserEnvironment(fetchedUserEnvironment));
          return fetchedUserEnvironment.user.orgs.filter(
            (o) => o.id === orgId
          )[0].searchApiKey;
        })
    )
      .then(({ facets }) => {
        dispatch({
          type: 'ORG_DASHBOARD_FACETS_SUCCESS',
          // @ts-expect-error fix me
          facets,
        });
      })
      .catch((error) => {
        throw error;
      });
  };
